import React from 'react';
import { Link } from 'react-router-dom';

import { useEvent } from '@legacy-providers/event';

import { useData } from '@shared/api/hooks/useData';
import { getDepartments } from '@shared/departments/endpoints/getDepartments';
import { deleteRegistration } from '@shared/registrations/endpoints/deleteRegistration';
import { getRegistrations } from '@shared/registrations/endpoints/getRegistrations';
import { Button } from '@shared/ui/components/Button';
import { DataTable } from '@shared/ui/components/DataTable';
import { Icon } from '@shared/ui/components/Icon';
import { Portlet } from '@shared/ui/components/Portlet';
import { Progress } from '@shared/ui/components/Progress';

import { AdminWrapper } from '@admin/layout/components/AdminWrapper';

import { StatusBadge } from './StatusBadge';
import { statusLabels } from './statusLabels';

type Registration = App.Domain.Registration.Data.RegistrationData;
type Department = App.Domain.Registration.Data.DepartmentData;

export function RegistrationsList() {
  const event = useEvent();

  const registrations = useData<Registration>({
    key: 'registrations',
    get: getRegistrations,
    query: { filter: { event_id: event.id } },
    defaults: { sort: '-updated_at' },
  });

  const typeOptions: { [key in Registration['company']['type']]: string } = {
    company: 'Company',
    artist: 'Artist',
    association: 'Association',
  };

  return (
    <AdminWrapper
      title="Registrations"
      actions={[
        <Button variant="outline" asChild>
          <Link to="/admin/registrations/import">
            <Icon icon="import" />
            Import
          </Link>
        </Button>,
        <Button asChild>
          <Link to="/admin/registrations/create">
            <Icon icon="plus" />
            Create
          </Link>
        </Button>,
      ]}
    >
      <Portlet>
        <DataTable<Registration>
          data={registrations}
          columns={[
            {
              name: 'company.name',
              label: 'Company',
              filterable: { name: 'company.name', icon: 'company' },
              sortable: false,
              render: (registration) => registration.company.name,
            },
            {
              name: 'status.name',
              label: 'Status',
              filterable: {
                name: 'status_name',
                icon: 'tag',
                type: 'select',
                options: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
              },
              sortable: false,
              render: (registration) => <StatusBadge registration={registration} />,
            },
            {
              name: 'completionPercentage',
              render: (registration) => <Progress value={registration.completionPercentage * 100} />,
            },
            {
              name: 'updatedAt',
              label: 'Last Updated',
              type: 'datetime',
            },
          ]}
          filters={[
            {
              label: 'Type',
              name: 'company.type',
              icon: 'tag',
              type: 'select',
              options: Object.entries(typeOptions).map(([value, label]) => ({ value, label })),
            },
            {
              label: 'Department',
              name: 'departments.id',
              type: 'select',
              options: ({ value }) =>
                getDepartments({ filter: { id: value } }).then((data) =>
                  data.data.map((department: Department) => ({
                    label: department.name,
                    value: department.id,
                  }))
                ),
            },
          ]}
          editUrl={(registration) => `/admin/registrations/${registration.id}/edit`}
          onDelete={deleteRegistration}
        />
      </Portlet>
    </AdminWrapper>
  );
}
